// The portfolio process progress widget: how many projects sit at each step of each process, which
// of them have gone quiet, and a click through to the projects at any one of them (REQ-013, REQ-014).
//
// ── Why the contract is mirrored here rather than imported ────────────────────────────────────────
// `Sisolco.Dashboards` defines `DashboardWidget<TConfig, TData>` in TypeScript, but the published
// package ships only page entry modules and hashed chunks: there is no static-asset URL from which a
// consumer can import the type or the `registerWidget` function it self-registers through. The
// interface below is therefore a structural mirror — the widget satisfies the library's contract by
// shape, which is all the registry needs, and the same absence is what
// `processPortfolioRegistration.ts` exists to work around. Both are recorded as a library gap.
//
// Plain DOM throughout: every value is written with `textContent`, never `innerHTML`. Project names
// and step labels are ordinary data a user can write, and a formatter that interpolated them into
// markup would be a stored-XSS sink of exactly the kind the library patched in its own chart
// tooltips.

import {
    buildDrillIntent,
    buildPortfolio,
    emptyStateText,
    type ProcessPortfolioBucket,
    type ProcessPortfolioConfig,
    type ProcessPortfolioRow,
} from "./processPortfolioModel";

/** The part of `WidgetRenderContext` this widget uses. Structural mirror; see the note above. */
export interface PortfolioRenderContext {
    emitClick(intent: unknown, rowScope?: Record<string, unknown>): void;
    cellClickIntent?: unknown;
    localize?(text: string | null | undefined): string;
    editMode?: boolean;
    permissionDenied?: boolean;
}

/** The lifecycle the dashboard host drives. Structural mirror of the library's contract. */
export interface PortfolioDashboardWidget {
    readonly widgetTypeKey: string;
    render(host: HTMLElement, config: ProcessPortfolioConfig, data: unknown, ctx: PortfolioRenderContext): void;
    update(config: ProcessPortfolioConfig, data: unknown): void;
    dispose(): void;
}

/**
 * Mirrors `KeyView.Projects.KeyViewProcessWidgetKeys.ProjectPortfolioProgress`, which both halves of
 * this widget are keyed on.
 *
 * ⚠️ It was `Sisolco.QA.QAProcessWidgetKeys.PortfolioProgress` until sprint 14, when the published
 * engine brought a provider of its own under that key. Two providers under one key make
 * `DashboardDataProviderRegistry` fail fast and take every dashboard in the app down, so KeyView's —
 * which is project-shaped rather than entity-generic, and carries the organisation scoping and the
 * drill-through — moved onto a key of its own rather than being deleted.
 */
export const ProcessPortfolioWidgetKey = "KeyView.ProjectProcessPortfolioProgress";

/** The host hands over whatever the provider returned; only an array of rows is usable. */
export function toRows(data: unknown): ProcessPortfolioRow[] {
    return Array.isArray(data) ? (data as ProcessPortfolioRow[]) : [];
}

export class ProcessPortfolioWidget implements PortfolioDashboardWidget {
    readonly widgetTypeKey = ProcessPortfolioWidgetKey;

    private host: HTMLElement | null = null;
    private root: HTMLElement | null = null;
    private titleEl: HTMLElement | null = null;
    private bodyEl: HTMLElement | null = null;
    private ctx: PortfolioRenderContext | null = null;

    render(host: HTMLElement, config: ProcessPortfolioConfig, data: unknown, ctx: PortfolioRenderContext): void {
        this.host = host;
        this.ctx = ctx;
        host.innerHTML = "";

        const root = document.createElement("div");
        root.className = "keyview-process-portfolio";

        const titleEl = document.createElement("div");
        titleEl.className = "title";

        const bodyEl = document.createElement("div");
        bodyEl.className = "portfolio-body";

        root.appendChild(titleEl);
        root.appendChild(bodyEl);
        host.appendChild(root);

        this.root = root;
        this.titleEl = titleEl;
        this.bodyEl = bodyEl;

        this.draw(config, data);
    }

    update(config: ProcessPortfolioConfig, data: unknown): void {
        if (!this.root) return;
        this.draw(config, data);
    }

    dispose(): void {
        if (this.host) this.host.innerHTML = "";
        this.host = null;
        this.root = null;
        this.titleEl = null;
        this.bodyEl = null;
        this.ctx = null;
    }

    private text(value: string | null | undefined): string {
        const localize = this.ctx?.localize;
        return localize ? localize(value) : (value ?? "");
    }

    private draw(config: ProcessPortfolioConfig, data: unknown): void {
        const body = this.bodyEl;
        if (!body) return;

        if (this.titleEl)
            this.titleEl.textContent = this.text(config?.Title ?? "Process progress");

        body.innerHTML = "";

        const rows = toRows(data);
        const { buckets, totalProjects, totalRows, unplaced } = buildPortfolio(rows, config);

        if (buckets.length === 0) {
            const empty = document.createElement("div");
            empty.className = "portfolio-empty";
            empty.textContent = emptyStateText(totalRows);
            body.appendChild(empty);
            return;
        }

        const list = document.createElement("ul");
        list.className = "portfolio-steps";

        for (const bucket of buckets)
            list.appendChild(this.drawBucket(bucket, config));

        body.appendChild(list);

        const summary = document.createElement("div");
        summary.className = "portfolio-summary";
        summary.textContent = unplaced > 0
            ? `${totalProjects} projects · ${unplaced} not yet at a step`
            : `${totalProjects} projects`;
        body.appendChild(summary);
    }

    private drawBucket(bucket: ProcessPortfolioBucket, config: ProcessPortfolioConfig): HTMLElement {
        const li = document.createElement("li");
        li.className = `portfolio-step portfolio-step-${bucket.kind}`;
        li.dataset.bucketKey = bucket.key;

        const label = document.createElement("span");
        label.className = "portfolio-step-label";
        label.textContent = bucket.processLabel
            ? `${this.text(bucket.processLabel)} — ${this.text(bucket.label)}`
            : this.text(bucket.label);
        // The label ellipsises rather than wrapping (site.css), because a wrapped label makes the
        // rows uneven heights and the counts stop lining up down the card. A 4-column cell is narrow
        // enough that "Project Management Procedure — Contract Handover Meeting" truncates almost
        // immediately, so the full text has to stay reachable: `title` is set from the SAME string
        // and is plain text, not markup, so it carries no more risk than the textContent above.
        label.title = label.textContent;
        li.appendChild(label);

        const count = document.createElement("span");
        count.className = "portfolio-step-count";
        count.textContent = String(bucket.projectCount);
        li.appendChild(count);

        if (bucket.stalledCount > 0) {
            const stalled = document.createElement("span");
            stalled.className = "portfolio-step-stalled";
            stalled.textContent = `${bucket.stalledCount} stalled`;
            li.appendChild(stalled);
        }

        // The drill-through. An authored cell-level intent wins — the dashboard author's wiring is
        // the point of the mechanism — and the widget's own openGrid intent is the fallback, so a
        // freshly-placed cell drills through without any authoring at all. Nothing is drawn as
        // clickable unless one of the two exists: a control that does nothing is worse than none.
        const intent = this.ctx?.cellClickIntent ?? buildDrillIntent(bucket, config);

        if (intent && !this.ctx?.editMode && !this.ctx?.permissionDenied) {
            li.classList.add("is-clickable");
            li.style.cursor = "pointer";
            li.addEventListener("click", () => {
                this.ctx?.emitClick(intent, {
                    BucketKey: bucket.key,
                    StepKey: bucket.kind === "step" ? bucket.key.split("::")[1] : null,
                    StepLabel: bucket.label,
                    ProcessLabel: bucket.processLabel ?? null,
                    ProjectIds: [...bucket.projectIds],
                    ProjectCount: bucket.projectCount,
                    StalledCount: bucket.stalledCount,
                });
            });
        }

        return li;
    }
}
